import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterControlsLayout } from '../../components/Accessibility';
import { FilterControls } from '../../components/admin/FilterControls';
import { useLoadingState } from '../../components/admin/Loading';
import { QuestionFiltersProvider, useQuestionFilters } from '../../components/admin/QuestionFilterContext';
import { QuestionSetsList } from '../../components/admin/QuestionSetsList';
import { WideAdminLayout } from '../../components/layout/AdminLayout';
import { withErrorAlerts } from '../../data/Errors';
import { QuestionSetOperations } from '../../data/Firestore';
import { gameConfigStore } from '../../store/GameConfigStore';
import { useAppDispatch } from '../../store/Hooks';
import { QuestionSetConfig, QuestionSetId } from '../../types/GameplayTypes';
import { pluralize } from '../../util/Text';

export const QuestionSetsScreen: FunctionComponent = () =>
	<QuestionFiltersProvider>
		<QuestionSetsScreenContent />
	</QuestionFiltersProvider>;

const QuestionSetsScreenContent: FunctionComponent = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { applyFilters } = useQuestionFilters();

	const [questionSets, setQuestionSets] = useState<QuestionSetConfig[]>([]);
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

	const { load: loadQuestionSets, loading, error } = useLoadingState({
		message: 'Loading Question Sets...', failureMessage: 'Failed to load Question Sets'
	}, async function () {
		const data = await QuestionSetOperations.getAll();
		setQuestionSets(data);
	});

	useEffect(function initialLoad() {
		void loadQuestionSets();
	}, [loadQuestionSets]);

	function openCreateScreen() {
		void navigate('/admin/question-sets/new');
	}

	function openEditScreen(id: QuestionSetId) {
		void navigate(`/admin/question-sets/${id}`);
	}

	function duplicate(id: QuestionSetId) {
		const set = questionSets.find(qs => qs.id === id);
		if (set) {
			void navigate('/admin/question-sets/new', { state: { template: set } });
		} else {
			throw new Error(`Unable to find QuestionSet ${id} for duplication`);
		}
	}

	function playQuestionSet(id: QuestionSetId) {
		const set = questionSets.find(qs => qs.id === id);
		if (set) {
			dispatch(gameConfigStore.actions.setQuestionSet(set));
			void navigate('/setup');
		} else {
			throw new Error(`Unable to find QuestionSet ${id} for duplication`);
		}
	}

	async function deleteSet(id: QuestionSetId) {
		if (confirm('Are you sure you want to delete this Question Set?')) {
			await withErrorAlerts('Failed to delete Question Set', async () => {
				await QuestionSetOperations.delete(id);
				await loadQuestionSets();
			});
		}
	}

	async function handleBulkDelete() {
		const selectedIds = Array.from(selectionModel.ids) as QuestionSetId[];
		if (!selectedIds.empty()
			&& confirm(`Delete ${pluralize(selectedIds.length, 'Question Set', { quantityPrefix: true })}?`)) {
			await withErrorAlerts('Failed to delete Question Sets', async () => {
				await Promise.all(
					selectedIds.map(id => QuestionSetOperations.delete(id))
				);
				setSelectionModel({ type: 'include', ids: new Set() });
				await loadQuestionSets();
			});
		}
	}

	// Get unique tags and authors for filters
	const allTags = questionSets.flatMap(qs => qs.tags).uniqueValues().sort();
	const allAuthors = questionSets.map(qs => qs.authorName).uniqueValues().sort();
	const filteredQuestionSets = applyFilters(questionSets);

	return <WideAdminLayout title='Question Sets' loading={loading} error={error} width='xl'>
		<FilterControlsLayout
			Controls={<FilterControls allTags={allTags} allAuthors={allAuthors} />}
			Buttons={<>
				{(selectionModel.ids.size > 0)
					&& <Button
						variant="outlined"
						color="error"
						startIcon={<DeleteIcon />}
						onClick={handleBulkDelete}
					>
						Delete {selectionModel.ids.size} selected
					</Button>}
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={openCreateScreen}
				>
					Create Question Set
				</Button>
			</>}
		/>

		<QuestionSetsList
			questionSets={filteredQuestionSets}
			selectionModel={selectionModel}
			onSelectionChange={setSelectionModel}
			onEdit={openEditScreen}
			onDelete={deleteSet}
			onDuplicate={duplicate}
			onPlay={playQuestionSet}
		/>
	</WideAdminLayout>;
};
